import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { CSVLink } from "react-csv";
import ApexChart from "react-apexcharts";
import config from '../config.js';
import { apiErrorMessage, detailToString } from '../utils/apiError';
import { desktopUi } from '../features/desktopUi';
import { loadUsers as fetchUsers, filterUsers, saveUser, markUserInactive } from '../features/users/userData';
import RememberedPhones from '../components/RememberedPhones';
import { adminPinFromSession } from '../utils/securityApi';

// Adjust to your environment
const API_BASE = config.API_BASE_URL;

// Camera relay configuration
const CAMERA_BASE_URL = "https://cameras.kancraftmfg.com";
const CF_ACCESS_CLIENT_ID = "f6aa5936667f527290786c8d7b896774.access";
const CF_ACCESS_CLIENT_SECRET = "53e231d91de00cb864cfe49535c6914434a4712bfd3fccfa4a20e3062d5799d8";
const getCfHeaders = () => ({
  'CF-Access-Client-Id': CF_ACCESS_CLIENT_ID,
  'CF-Access-Client-Secret': CF_ACCESS_CLIENT_SECRET
});

// Map timeline workstation names to camera station names
const CAMERA_STATION_MAP = {
  'Laser': 'Laser',
  'Laser - Fault': 'Laser',
  'Brake': 'Brake',
  'Fabrication': 'Fabrication',
  'Blast': 'Blast',
  'Finish': 'Shipping',
};

function resolveStation(block, allBlocks) {
  if (block.workstation !== 'Inactive') {
    return CAMERA_STATION_MAP[block.workstation] || null;
  }
  const idx = allBlocks.indexOf(block);
  for (let i = idx - 1; i >= 0; i--) {
    if (allBlocks[i].workstation !== 'Inactive') {
      return CAMERA_STATION_MAP[allBlocks[i].workstation] || null;
    }
  }
  return null;
}

function parseClipTime(filename) {
  const m = filename.match(/^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})\.mp4$/);
  if (!m) return null;
  return new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3]), parseInt(m[4]), parseInt(m[5]), parseInt(m[6]));
}

function formatClipTime(filename) {
  const dt = parseClipTime(filename);
  if (!dt) return filename;
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true
  }).format(dt);
}

// Recorder writes ~2-min segments; allow slack when testing whether a clip spans a time.
const SEGMENT_SEC = 125;

// Each camera at a station records its own ~2-min segments independently and the
// recorders start a few dozen seconds apart, so segment filenames don't line up
// across cameras. Given a master clip (camera index 0), pick the clip from another
// camera that covers the master clip's START time and report how far to seek into it
// (offsetSec) so that pane shows the SAME wall-clock moment as the master at t=0.
// Falls back to the nearest clip by start time, within a tolerance, when none spans it.
function alignedClipForIndex(masterFilename, clips, toleranceMs = 90000) {
  const t0 = parseClipTime(masterFilename);
  if (!t0 || !Array.isArray(clips) || clips.length === 0) return null;
  const t0ms = t0.getTime();

  // Prefer the latest clip whose [start, start+SEGMENT) window contains t0.
  let containing = null;
  let containingStart = -Infinity;
  for (const f of clips) {
    const t = parseClipTime(f);
    if (!t) continue;
    const s = t.getTime();
    if (s <= t0ms && t0ms < s + SEGMENT_SEC * 1000 && s > containingStart) {
      containing = f; containingStart = s;
    }
  }
  if (containing) {
    const offsetSec = Math.max(0, Math.min((t0ms - containingStart) / 1000, SEGMENT_SEC));
    return { filename: containing, offsetSec };
  }

  // Otherwise the nearest clip by start time, within tolerance (offset clamped ≥ 0).
  let best = null;
  let bestDiff = Infinity;
  let bestStart = 0;
  for (const f of clips) {
    const t = parseClipTime(f);
    if (!t) continue;
    const diff = Math.abs(t.getTime() - t0ms);
    if (diff < bestDiff) { bestDiff = diff; best = f; bestStart = t.getTime(); }
  }
  if (best && bestDiff <= toleranceMs) {
    return { filename: best, offsetSec: Math.max(0, Math.min((t0ms - bestStart) / 1000, SEGMENT_SEC)) };
  }
  return null;
}

// Helper function to format decimal hours into HH:MM
function formatDecimalHoursToHHMM(decimalHours) {
    if (isNaN(decimalHours) || decimalHours < 0) {
        return "00:00";
    }
    const totalMinutes = Math.round(parseFloat(decimalHours) * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    const paddedHours = String(hours).padStart(2, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');
    
    return `${paddedHours}:${paddedMinutes}`;
}

// Helper to format time for the timeline chart tooltip
const formatTimelineTooltip = (time) => new Date(time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

// Colors for different workstations
const workstationColors = {
    'Laser': '#008FFB',
    'Laser - Fault': '#FF6B6B',
    'Tap': '#00E396',
    'Brake': '#FEB019',
    'Fabrication': '#FF4560',
    'Blast': '#775DD0',
    'Finish': '#546E7A',
    'Inactive': '#D3D3D3',
    'default': '#808080'
};
const workstationBarColors = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28'];


// Custom tooltip component for the Bar Chart to show totals and utilization
const CustomBarChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const dataPoint = payload[0].payload;
    const totalPunchedHours = dataPoint.totalPunchedHours || 0;
    const kancraftHours = dataPoint.kancraft_hours || 0;
    const faultHours = dataPoint.fault_hours || 0;
    const utilizationRate = dataPoint.utilization_rate;
    const adjustedUtilizationRate = dataPoint.adjusted_utilization_rate;

    return (
      <div className="custom-tooltip p-2 border bg-white rounded shadow-sm" style={{fontFamily: 'sans-serif', fontSize: '12px'}}>
        <p className="label fw-bold mb-1">{`${label}`}</p>
        {payload.filter(p => p.type !== 'line' && p.value > 0).map((p, index) => (
          <p key={index} className="intro" style={{ color: p.fill, margin: '0 0 3px 0' }}>
            {`${p.name}: ${formatDecimalHoursToHHMM(p.value)}`}
          </p>
        ))}
        <hr className="my-1" />
        <p className="total fw-bold mb-0">Total Punched: {formatDecimalHoursToHHMM(totalPunchedHours)}</p>
        {faultHours > 0 && (
            <p className="total fw-bold mb-0" style={{color: '#FF6B6B'}}>
                Laser Fault: {formatDecimalHoursToHHMM(faultHours)}
            </p>
        )}
        <p className="total fw-bold mb-0">Timesheet Hours: {formatDecimalHoursToHHMM(kancraftHours)}</p>
        {utilizationRate !== null && (
            <p className="total fw-bold mb-0" style={{color: '#008FFB'}}>
                Utilization: {utilizationRate.toFixed(1)}%
            </p>
        )}
        {faultHours > 0 && adjustedUtilizationRate !== null && (
            <p className="total fw-bold mb-0" style={{color: '#FF6B6B'}}>
                Adjusted (incl. fault): {adjustedUtilizationRate.toFixed(1)}%
            </p>
        )}
      </div>
    );
  }
  return null;
};

// Labor hours arrive automatically from TimeClock (time.badideaco.com) once a
// day. This panel is the status board for that: what the last run did, which
// JobQ users are linked to which TimeClock employee, and who is not linked and
// therefore has no hours in JobQ at all. The Gusto CSV upload below it is kept
// only as a manual fallback — Gusto stopped being the source of truth for time
// at the 2026-07-25 payroll cutover.
function TimesheetSyncPanel() {
    const [links, setLinks] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState(null);
    const [savingUser, setSavingUser] = useState(null);
    const [showLegacy, setShowLegacy] = useState(false);
    const [showAllUsers, setShowAllUsers] = useState(false);

    const loadLinks = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/timesheets/timeclock_links`);
            const data = await res.json();
            if (!res.ok) throw new Error(detailToString(data.detail) || "Could not load TimeClock links");
            setLinks(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadLinks(); }, [loadLinks]);

    const handleSyncNow = async (days) => {
        setSyncing(true);
        setSyncResult(null);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/timesheets/sync_timeclock?days=${days}`, { method: "POST" });
            const data = await res.json();
            if (!res.ok) throw new Error(detailToString(data.detail) || "Sync failed");
            setSyncResult(data);
            loadLinks();
        } catch (err) {
            setError(err.message);
        } finally {
            setSyncing(false);
        }
    };

    const handleIgnore = async (employee) => {
        if (!window.confirm(
            `Stop warning about ${employee.name}?\n\n` +
            `Their hours will NOT be imported into JobQ — this is for people who ` +
            `never run a JobQ task. They stay listed below so the decision is ` +
            `visible, and you can undo it any time.`)) return;
        setSavingUser(employee.timeclock_employee_id);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/timesheets/timeclock_ignore`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    timeclock_employee_id: employee.timeclock_employee_id,
                    employee_name: employee.name,
                    reason: "Never runs JobQ tasks",
                }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(detailToString(data.detail) || "Could not suppress this employee");
            await loadLinks();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingUser(null);
        }
    };

    const handleUnignore = async (employee) => {
        setSavingUser(employee.timeclock_employee_id);
        setError(null);
        try {
            const res = await fetch(
                `${API_BASE}/timesheets/timeclock_ignore/${employee.timeclock_employee_id}`,
                { method: "DELETE" });
            const data = await res.json();
            if (!res.ok) throw new Error(detailToString(data.detail) || "Could not restore this employee");
            await loadLinks();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingUser(null);
        }
    };

    const handleLinkChange = async (userId, timeclockEmployeeId) => {
        setSavingUser(userId);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/users/${userId}/timeclock_link`, {
                method: "PUT",
                headers: { "Content-Type": "application/json", "X-Admin-Pin": adminPinFromSession() || "" },
                body: JSON.stringify({ timeclock_employee_id: timeclockEmployeeId || null }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(detailToString(data.detail) || "Could not save the link");
            await loadLinks();
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingUser(null);
        }
    };

    const employees = links?.timeclock_employees || [];
    const users = links?.users || [];
    const unlinkedWithHours = employees.filter(e => !e.linked && !e.ignored && e.hours_30d > 0);
    const suppressed = links?.ignored_employees || [];
    const linkedUsers = users.filter(u => u.timeclock_employee_id);
    const visibleUsers = showAllUsers ? users : linkedUsers;
    const lastSynced = linkedUsers
        .map(u => u.last_synced_at).filter(Boolean).sort().slice(-1)[0];
    const latestDay = linkedUsers
        .map(u => u.last_work_date).filter(Boolean).sort().slice(-1)[0];

    // work_date is a plain calendar date ("2026-07-30"). new Date() reads that
    // as UTC midnight and toLocaleDateString then renders it in Central, which
    // shows the PREVIOUS day — "hours through 7/29" on the evening of a day
    // whose hours are already in. Format the parts directly instead.
    const fmtDate = (v) => {
        if (!v) return "—";
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
        if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
        return new Date(v).toLocaleDateString();
    };
    // updated_at is a real timestamptz — the default parse is correct here.
    const fmtDateTime = (v) => (v ? new Date(v).toLocaleString() : "never");

    return (
        <div className="mt-4">
            <div className="p-3 border rounded mb-3">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                        <h5 className="mb-1">Labor Hours from TimeClock</h5>
                        <p className="text-muted small mb-0">
                            Hours import automatically once a day from the time clock. Nothing to
                            upload. These are the hours the utilization figure on each user's
                            report is measured against.
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        <button className="btn btn-outline-secondary btn-sm" onClick={loadLinks} disabled={loading || syncing}>
                            Refresh
                        </button>
                        <button className="btn btn-primary" onClick={() => handleSyncNow(14)} disabled={syncing}>
                            {syncing ? "Syncing…" : "Sync now"}
                        </button>
                    </div>
                </div>

                <div className="row mt-3 g-3">
                    <div className="col-auto">
                        <div className="text-muted small">Hours through</div>
                        <div className="fw-bold">{fmtDate(latestDay)}</div>
                    </div>
                    <div className="col-auto">
                        <div className="text-muted small">Last import</div>
                        <div className="fw-bold">{fmtDateTime(lastSynced)}</div>
                    </div>
                    <div className="col-auto">
                        <div className="text-muted small">Linked users</div>
                        <div className="fw-bold">{linkedUsers.length}</div>
                    </div>
                </div>

                {links?.timeclock_error && (
                    <div className="alert alert-warning mt-3 mb-0">
                        Could not reach TimeClock: {links.timeclock_error} — the links below are
                        still accurate, but the employee list could not be loaded.
                    </div>
                )}
                {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}

                {syncResult && (
                    <div className="alert alert-success mt-3 mb-0">
                        <strong>Synced {syncResult.window.start} → {syncResult.window.end}</strong>
                        {" — "}{syncResult.inserted} added, {syncResult.updated} updated,
                        {" "}{syncResult.unchanged} unchanged
                        {syncResult.deleted > 0 && `, ${syncResult.deleted} removed`}
                        {" "}across {syncResult.users_synced} user(s).
                    </div>
                )}
            </div>

            {unlinkedWithHours.length > 0 && (
                <div className="alert alert-warning">
                    <strong>{unlinkedWithHours.length} employee(s) on the time clock are not
                    linked to a JobQ user.</strong> Their hours are not in JobQ, so their
                    utilization cannot be calculated. Link them in the table below — or, if
                    they never run a JobQ task, stop warning about them.
                    <ul className="list-unstyled mb-0 mt-2">
                        {unlinkedWithHours.map(e => (
                            <li key={e.timeclock_employee_id} className="d-flex align-items-center gap-2 mb-1">
                                <span>{e.name} — {e.hours_30d} h in the last 30 days</span>
                                <button className="btn btn-sm btn-outline-secondary py-0"
                                        disabled={savingUser === e.timeclock_employee_id}
                                        onClick={() => handleIgnore(e)}>
                                    Never track in JobQ
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {suppressed.length > 0 && (
                <div className="p-2 px-3 border rounded mb-3 bg-light">
                    <div className="small text-muted">
                        <strong>Not tracked in JobQ ({suppressed.length})</strong> — deliberately
                        excluded, no hours imported and no warnings.
                    </div>
                    <ul className="list-unstyled mb-0 mt-1">
                        {suppressed.map(e => (
                            <li key={e.timeclock_employee_id}
                                className="d-flex align-items-center gap-2 small text-muted">
                                <span>
                                    {e.employee_name}
                                    {e.reason ? ` — ${e.reason}` : ""}
                                </span>
                                <button className="btn btn-sm btn-link py-0 px-1"
                                        disabled={savingUser === e.timeclock_employee_id}
                                        onClick={() => handleUnignore(e)}>
                                    Start tracking
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="p-3 border rounded">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">TimeClock links</h6>
                    <div className="form-check form-switch">
                        <input className="form-check-input" type="checkbox" id="showAllUsers"
                               checked={showAllUsers} onChange={(e) => setShowAllUsers(e.target.checked)} />
                        <label className="form-check-label small" htmlFor="showAllUsers">
                            Show unlinked users ({users.length - linkedUsers.length})
                        </label>
                    </div>
                </div>
                {loading ? <p className="text-muted mb-0">Loading…</p> : (
                    <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                            <thead>
                                <tr>
                                    <th>JobQ user</th>
                                    <th>TimeClock employee</th>
                                    <th className="text-end">Hours through</th>
                                    <th className="text-end">Days on file</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleUsers.map(u => (
                                    <tr key={u.user_id} className={u.is_active ? "" : "text-muted"}>
                                        <td>
                                            {u.name}
                                            {!u.is_active && <span className="badge bg-secondary ms-2">inactive</span>}
                                        </td>
                                        <td style={{ minWidth: 240 }}>
                                            <select
                                                className="form-select form-select-sm"
                                                value={u.timeclock_employee_id || ""}
                                                disabled={savingUser === u.user_id || employees.length === 0}
                                                onChange={(e) => handleLinkChange(u.user_id, e.target.value)}
                                            >
                                                <option value="">— not linked —</option>
                                                {employees.map(e => (
                                                    <option key={e.timeclock_employee_id} value={e.timeclock_employee_id}
                                                            disabled={e.linked && e.timeclock_employee_id !== u.timeclock_employee_id}>
                                                        {e.name}
                                                        {e.linked && e.timeclock_employee_id !== u.timeclock_employee_id ? " (already linked)" : ""}
                                                        {/* Still selectable — linking clears the ignore server-side. */}
                                                        {e.ignored ? " (not tracked)" : ""}
                                                    </option>
                                                ))}
                                                {/* A link whose employee is not in the last 30 days of activity
                                                    would otherwise vanish from the dropdown and read as unlinked. */}
                                                {u.timeclock_employee_id &&
                                                 !employees.some(e => e.timeclock_employee_id === u.timeclock_employee_id) && (
                                                    <option value={u.timeclock_employee_id}>
                                                        (linked — no recent hours)
                                                    </option>
                                                )}
                                            </select>
                                        </td>
                                        <td className="text-end">{fmtDate(u.last_work_date)}</td>
                                        <td className="text-end">{u.rows || 0}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="mt-3">
                <button className="btn btn-link btn-sm px-0" onClick={() => setShowLegacy(!showLegacy)}>
                    {showLegacy ? "Hide" : "Show"} legacy Gusto CSV import
                </button>
                {showLegacy && <TimesheetUploader />}
            </div>
        </div>
    );
}

// Legacy manual import. Superseded by the automatic TimeClock sync above.
function TimesheetUploader() {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [response, setResponse] = useState(null);
    const [error, setError] = useState(null);

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!file) {
            alert("Please select a file to upload.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        setUploading(true);
        setResponse(null);
        setError(null);

        try {
            const res = await fetch(`${API_BASE}/timesheets/upload`, {
                method: "POST",
                body: formData,
            });
            const data = await res.json();
            if (!res.ok) {
                throw new Error(detailToString(data.detail) || "Upload failed");
            }
            setResponse(data);
            alert(`Upload successful! ${data.new_entries} new entries, ${data.updated_entries} updated.`);
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="mt-4 p-3 border rounded">
            <h5>Upload Timesheet CSV</h5>
            <p className="text-muted small">
                Manual fallback only. Gusto stopped being the source of truth for time at the
                2026-07-25 payroll cutover, so this will not reflect current hours — use the
                automatic TimeClock sync above. Kept for re-importing a historical Gusto report.
            </p>
            <div className="mb-3">
                <input className="form-control" type="file" accept=".csv" onChange={handleFileChange} />
            </div>
            <button className="btn btn-primary" onClick={handleUpload} disabled={uploading}>
                {uploading ? "Uploading..." : "Upload"}
            </button>

            {response && (
                <div className="alert alert-success mt-3">
                    <p><strong>Upload Complete</strong></p>
                    <p>Users found in CSV: {response.users_found_in_csv}</p>
                    <p>New entries created: {response.new_entries}</p>
                    <p>Entries updated: {response.updated_entries}</p>
                </div>
            )}
            {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>
    );
}

// --- NEW: Utilization Gauge Component ---
const UtilizationGauge = ({ title, utilization }) => {
    const getUtilColor = (util) => {
        if (util < 50) return "#FF4560"; // Red
        if (util < 75) return "#FEB019"; // Yellow
        return "#00E396"; // Green
    };

    const color = getUtilColor(utilization);
    const percentage = Math.min(Math.max(utilization, 0), 100);
    const strokeDasharray = `${percentage}, 100`;

    return (
        <div className="text-center">
            <svg width="120" height="120" viewBox="0 0 36 36" className="mx-auto">
                <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e6e6e6"
                    strokeWidth="3"
                />
                <path
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke={color}
                    strokeWidth="3"
                    strokeDasharray={strokeDasharray}
                    strokeLinecap="round"
                    transform="rotate(-90 18 18)"
                />
                <text x="18" y="20.35" className="fw-bold" fill="#333" fontSize="0.5em" textAnchor="middle">
                    {utilization.toFixed(1)}%
                </text>
            </svg>
            <h6 className="mt-2 mb-0 text-muted">{title}</h6>
        </div>
    );
};

// --- NEW: Component to fetch and display utilization badges ---
const UtilizationBadges = ({ selectedUser }) => {
    const [utilizationData, setUtilizationData] = useState({
        lastWeek: null,
        lastMonth: null,
        thisYear: null,
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!selectedUser) return;

        const fetchUtilizationData = async (userId, startDate, endDate) => {
            try {
                const res = await fetch(
                    `${API_BASE}/reports/user_task_time_by_station?user_id=${userId}&start_date=${startDate}&end_date=${endDate}&group_by=month`
                );
                if (!res.ok) return 0;
                const apiData = await res.json();
                
                if (!apiData || apiData.length === 0) return 0;

                const totalPunchedSeconds = apiData.reduce((sum, period) => {
                    return sum + Object.values(period.workstations).reduce((wsSum, s) => wsSum + s, 0);
                }, 0);
                const totalTimesheetHours = apiData.reduce((sum, period) => sum + period.kancraft_hours, 0);
                const totalPunchedHours = totalPunchedSeconds / 3600;
                
                return totalTimesheetHours > 0 ? (totalPunchedHours / totalTimesheetHours) * 100 : 0;
            } catch (error) {
                console.error("Failed to fetch utilization data:", error);
                return 0;
            }
        };

        const today = new Date();
        const toISO = (date) => date.toISOString().split('T')[0];

        // Date ranges
        const lastWeekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay() - 6);
        const lastWeekEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay());
        
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

        const thisYearStart = new Date(today.getFullYear(), 0, 1);

        setLoading(true);
        Promise.all([
            fetchUtilizationData(selectedUser.user_id, toISO(lastWeekStart), toISO(lastWeekEnd)),
            fetchUtilizationData(selectedUser.user_id, toISO(lastMonthStart), toISO(lastMonthEnd)),
            fetchUtilizationData(selectedUser.user_id, toISO(thisYearStart), toISO(today)),
        ]).then(([lastWeek, lastMonth, thisYear]) => {
            setUtilizationData({ lastWeek, lastMonth, thisYear });
            setLoading(false);
        });

    }, [selectedUser]);

    if (loading || !selectedUser) {
        return <div className="text-center p-3">Loading Utilization...</div>;
    }
    
    if (utilizationData.lastWeek === null) return null;

    return (
        <div className="p-3 border rounded mb-4">
            <h5 className="mb-3 text-center">At a Glance Utilization</h5>
            <div className="d-flex justify-content-around">
                <UtilizationGauge title="Last Week" utilization={utilizationData.lastWeek} />
                <UtilizationGauge title="Last Month" utilization={utilizationData.lastMonth} />
                <UtilizationGauge title="This Year" utilization={utilizationData.thisYear} />
            </div>
        </div>
    );
};

export default function UserManagementPage() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Form fields
  const [name, setName] = useState("");
  const [role, setRole] = useState("Operator");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [hourlyWage, setHourlyWage] = useState(0); // New state for Hourly Wage

  // Search and Filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [showInactive, setShowInactive] = useState(false);

  // For error handling & loading
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // State for the Bar Chart
  const [chartData, setChartData] = useState({ data: [], workstations: [] });
  const [groupBy, setGroupBy] = useState('day');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);

  // State for CSV Export
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);

  // State for the Timeline Chart
  const [timelineDate, setTimelineDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [timelineData, setTimelineData] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  // Camera playback modal state
  const [camModalOpen, setCamModalOpen] = useState(false);
  const [camModalBlock, setCamModalBlock] = useState(null);
  const [camModalStation, setCamModalStation] = useState("");
  const [camModalCamCount, setCamModalCamCount] = useState(1);          // # of cameras at the resolved station
  const [camModalClips, setCamModalClips] = useState([]);               // master timeline = camera index 0
  const [camModalClipsByIndex, setCamModalClipsByIndex] = useState({}); // { 0: [...], 1: [...], 2: [...] }
  const [camModalSelectedClip, setCamModalSelectedClip] = useState("");
  const [camModalVideoUrls, setCamModalVideoUrls] = useState([]);       // blob URL per camera index ('' = none)
  const [camModalVideoLoading, setCamModalVideoLoading] = useState(false);
  const [camModalClipsLoading, setCamModalClipsLoading] = useState(false);
  const [camModalThumbs, setCamModalThumbs] = useState({});
  const camVideoRefs = useRef([]);                                      // <video> els per camera, for play/seek sync

  // --- Filtered and Sorted Users ---
  // Name search, inactive only when asked, by name (features/users/userData).
  const filteredUsers = useMemo(() => filterUsers(users, { searchTerm, showInactive }), [users, searchTerm, showInactive]);

  // Fetch bar chart data when controls change
  useEffect(() => {
    if (selectedUser) {
      fetchChartData();
    } else {
      setChartData({ data: [], workstations: [] });
    }
  }, [selectedUser, startDate, endDate, groupBy]);
  
  // Fetch timeline data when user or date changes
  useEffect(() => {
    if (selectedUser) {
        fetchUserTimelineData();
    } else {
        setTimelineData([]);
    }
  }, [selectedUser, timelineDate]);

  // Prepare CSV data when bar chart data changes
  useEffect(() => {
      if (chartData && chartData.data.length > 0) {
          const hasFault = chartData.data.some(d => d.fault_hours > 0);
          const headers = [
              { label: "Period", key: "name" },
              ...chartData.workstations.map(ws => ({ label: ws, key: ws })),
              ...(hasFault ? [{ label: "Laser Fault Hours", key: "fault_hours_fmt" }] : []),
              { label: "Timesheet Hours", key: "kancraft_hours" },
              { label: "Utilization %", key: "utilization_rate_fmt" },
              ...(hasFault ? [{ label: "Adjusted Utilization %", key: "adjusted_utilization_rate_fmt" }] : []),
          ];
          setCsvHeaders(headers);
          setCsvData(chartData.data.map(d => ({
              ...d,
              fault_hours_fmt: d.fault_hours ? d.fault_hours.toFixed(2) : '0',
              utilization_rate_fmt: d.utilization_rate ? d.utilization_rate.toFixed(2) : 'N/A',
              adjusted_utilization_rate_fmt: d.adjusted_utilization_rate ? d.adjusted_utilization_rate.toFixed(2) : 'N/A',
          })));
      } else {
          setCsvData([]);
          setCsvHeaders([]);
      }
  }, [chartData]);


  async function fetchChartData() {
    if (!selectedUser) return;

    setChartLoading(true);
    setChartError(null);
    try {
      const res = await fetch(
        `${API_BASE}/reports/user_task_time_by_station?user_id=${selectedUser.user_id}&start_date=${startDate}&end_date=${endDate}&group_by=${groupBy}`
      );
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res, "Failed to fetch chart data"));
      }
      const apiData = await res.json();
      const transformed = transformDataForChart(apiData);
      setChartData(transformed);
    } catch (err) {
      setChartError(err.message);
      setChartData({ data: [], workstations: [] });
    } finally {
      setChartLoading(false);
    }
  }

  // function to fetch data for the user activity timeline
  async function fetchUserTimelineData() {
    if (!selectedUser || !timelineDate) return;
    setTimelineLoading(true);
    setTimelineError(null);
    try {
        const url = `${API_BASE}/reports/user_activity_timeline/${selectedUser.user_id}/${timelineDate}`;
        const res = await fetch(url);
        if (!res.ok) {
            throw new Error(await apiErrorMessage(res, "Failed to fetch timeline data"));
        }
        const data = await res.json();
        setTimelineData(data || []);
    } catch (err) {
        setTimelineError(err.message);
        setTimelineData([]);
    } finally {
        setTimelineLoading(false);
    }
  }

  function transformDataForChart(apiData) {
    if (!apiData || apiData.length === 0) return { data: [], workstations: [] };

    const dataMap = new Map();
    const workstationSet = new Set();

    apiData.forEach(({ period, workstations, kancraft_hours, fault_seconds }) => {
        const dateLabel = new Date(period).toLocaleDateString();
        if (!dataMap.has(dateLabel)) {
            dataMap.set(dateLabel, {
                name: dateLabel,
                isoDate: period,
                workstations: {},
                kancraft_hours: 0,
                fault_seconds: 0
            });
        }
        const periodData = dataMap.get(dateLabel);

        for (const [ws, seconds] of Object.entries(workstations)) {
            workstationSet.add(ws);
            periodData.workstations[ws] = (periodData.workstations[ws] || 0) + seconds;
        }
        periodData.kancraft_hours += kancraft_hours;
        periodData.fault_seconds += (fault_seconds || 0);
    });

    let finalData = Array.from(dataMap.values()).map(period => {
        const totalPunchedSeconds = Object.values(period.workstations).reduce((sum, s) => sum + s, 0);
        const totalPunchedHours = totalPunchedSeconds / 3600;
        const faultHours = (period.fault_seconds || 0) / 3600;

        let utilizationRate = null;
        let adjustedUtilizationRate = null;
        if (period.kancraft_hours > 0) {
            utilizationRate = (totalPunchedHours / period.kancraft_hours) * 100;
            adjustedUtilizationRate = ((totalPunchedHours + faultHours) / period.kancraft_hours) * 100;
        }

        const dataPoint = {
            name: period.name,
            isoDate: period.isoDate,
            kancraft_hours: period.kancraft_hours,
            utilization_rate: utilizationRate,
            adjusted_utilization_rate: adjustedUtilizationRate,
            totalPunchedHours: totalPunchedHours,
            fault_hours: faultHours
        };

        workstationSet.forEach(ws => {
            dataPoint[ws] = ((period.workstations[ws] || 0) / 3600);
        });
        return dataPoint;
    });
    
    // **FIX**: Sort the final data by date chronologically
    finalData.sort((a, b) => new Date(a.isoDate) - new Date(b.isoDate));

    return {
      data: finalData,
      workstations: Array.from(workstationSet).sort()
    };
  }

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUsers(); // features/users/userData
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setName("");
    setRole("Operator");
    setPin("");
    setConfirmPin("");
    setHourlyWage(0);
    setIsActive(true);
    setSelectedUser(null);
  }

  function handleSelectUser(u) {
    setSelectedUser(u);
    setName(u.name || "");
    setRole(u.role || "Operator");
    setPin("");
    setConfirmPin("");
    setHourlyWage(u.hourly_wage || 0);
    setIsActive(u.is_active);
    setActiveTab('details'); // Reset to details tab on user selection
  }

  // features/users/userData (the phone's too): the checks, the body, POST / PATCH, the alerts; a refusal
  // comes back for the banner.
  const busyFlag = (busy) => { setLoading(busy); if (busy) setError(null); };

  async function handleSave() {
    const r = await saveUser({
      userId: selectedUser ? selectedUser.user_id : null,
      form: { name, role, pin, confirm_pin: confirmPin, is_active: isActive, hourly_wage: hourlyWage },
      ui: desktopUi,
      setBusy: busyFlag,
    });
    if (r.error) setError(r.error);
    if (r.done) {
      loadUsers();
      resetForm();
    }
  }

  async function handleMarkInactive(u) {
    const r = await markUserInactive({ user: u, ui: desktopUi, setBusy: busyFlag });
    if (r.error) setError(r.error);
    if (r.done) {
      loadUsers();
      if (selectedUser && selectedUser.user_id === u.user_id) {
        resetForm();
      }
    }
  }

  // Calculate totals for the selected date range
  const reportTotals = useMemo(() => {
    if (!chartData.data || chartData.data.length === 0) {
        return { totalPunched: 0, totalTimesheet: 0, utilization: 0, totalFault: 0, adjustedUtilization: 0 };
    }

    const totalPunched = chartData.data.reduce((sum, item) => sum + (item.totalPunchedHours || 0), 0);
    const totalTimesheet = chartData.data.reduce((sum, item) => sum + (item.kancraft_hours || 0), 0);
    const totalFault = chartData.data.reduce((sum, item) => sum + (item.fault_hours || 0), 0);

    const utilization = totalTimesheet > 0 ? (totalPunched / totalTimesheet) * 100 : 0;
    const adjustedUtilization = totalTimesheet > 0 ? ((totalPunched + totalFault) / totalTimesheet) * 100 : 0;

    return { totalPunched, totalTimesheet, utilization, totalFault, adjustedUtilization };
  }, [chartData.data]);


  const { totalTimelineHours, totalFaultHours } = React.useMemo(() => {
    const calcHours = (tasks) => {
        if (tasks.length === 0) return 0;
        return tasks.reduce((sum, task) => {
            const start = new Date(task.start_time).getTime();
            const end = new Date(task.end_time).getTime();
            return sum + (end - start);
        }, 0) / (1000 * 60 * 60);
    };
    const activeTasks = timelineData.filter(task => task.workstation !== 'Inactive' && task.workstation !== 'Laser - Fault');
    const faultTasks = timelineData.filter(task => task.workstation === 'Laser - Fault');
    return { totalTimelineHours: calcHours(activeTasks), totalFaultHours: calcHours(faultTasks) };
  }, [timelineData]);

  // Camera modal handlers
  const handleTimelineClick = useCallback(async (block) => {
    const station = resolveStation(block, timelineData);
    if (!station) {
      setCamModalBlock(block);
      setCamModalStation("");
      setCamModalCamCount(1);
      setCamModalClips([]);
      setCamModalClipsByIndex({});
      setCamModalSelectedClip("");
      setCamModalOpen(true);
      return;
    }

    setCamModalBlock(block);
    setCamModalStation(station);
    setCamModalCamCount(1);
    setCamModalClips([]);
    setCamModalClipsByIndex({});
    setCamModalSelectedClip("");
    setCamModalVideoUrls([]);
    setCamModalThumbs({});
    setCamModalOpen(true);
    setCamModalClipsLoading(true);

    try {
      const date = block.start_time.split('T')[0];
      const taskStart = new Date(block.start_time).getTime() - 120000;
      const taskEnd = new Date(block.end_time).getTime();
      const inWindow = (f) => {
        const clipTime = parseClipTime(f);
        if (!clipTime) return false;
        const clipMs = clipTime.getTime();
        return clipMs >= taskStart && clipMs <= taskEnd;
      };

      // How many cameras does this station have? (Laser = 3, most others = 1)
      let camCount = 1;
      try {
        const cfgRes = await fetch(`${CAMERA_BASE_URL}/cameras/config`, { headers: getCfHeaders(), credentials: 'include' });
        if (cfgRes.ok) {
          const cfg = await cfgRes.json();
          camCount = Math.max(1, parseInt(cfg?.[station], 10) || 1);
        }
      } catch { /* fall back to a single camera */ }
      setCamModalCamCount(camCount);

      // Fetch each camera's clip list for the day, filtered to the task window.
      const clipsByIndex = {};
      await Promise.all(Array.from({ length: camCount }).map(async (_, i) => {
        try {
          const url = `${CAMERA_BASE_URL}/recordings/list?station=${encodeURIComponent(station)}&index=${i}&date=${date}&token=${CF_ACCESS_CLIENT_SECRET}`;
          const res = await fetch(url, { headers: getCfHeaders(), credentials: 'include' });
          if (!res.ok) { clipsByIndex[i] = []; return; }
          const data = await res.json();
          clipsByIndex[i] = (data?.files || []).filter(inWindow).reverse();
        } catch {
          clipsByIndex[i] = [];
        }
      }));

      const master = clipsByIndex[0] || [];
      setCamModalClipsByIndex(clipsByIndex);
      setCamModalClips(master);
      if (master.length > 0) {
        setCamModalSelectedClip(master[0]);
      }

      // Thumbnails for the master (camera 0) timeline filmstrip.
      const thumbs = {};
      await Promise.all(master.map(async (f) => {
        try {
          const thumbUrl = `${CAMERA_BASE_URL}/recordings/thumb?station=${encodeURIComponent(station)}&index=0&filename=${encodeURIComponent(f)}&token=${CF_ACCESS_CLIENT_SECRET}`;
          const thumbRes = await fetch(thumbUrl, { headers: getCfHeaders(), credentials: 'include' });
          if (thumbRes.ok) {
            const blob = await thumbRes.blob();
            thumbs[f] = URL.createObjectURL(blob);
          }
        } catch { /* skip */ }
      }));
      setCamModalThumbs(thumbs);
    } catch (err) {
      console.error("Failed to load camera clips", err);
    } finally {
      setCamModalClipsLoading(false);
    }
  }, [timelineData]);

  useEffect(() => {
    if (!camModalOpen || !camModalSelectedClip || !camModalStation) return;
    let cancelled = false;
    const createdUrls = [];

    async function loadAllCamVideos() {
      setCamModalVideoUrls([]);
      setCamModalVideoLoading(true);

      const count = Math.max(1, camModalCamCount);
      const panes = Array.from({ length: count }, () => ({ url: '', offset: 0 }));
      await Promise.all(Array.from({ length: count }).map(async (_, i) => {
        // Camera 0 plays the selected master clip from its start; the others play the
        // segment that spans the same instant, seeked by offset to stay wall-clock synced.
        let filename = camModalSelectedClip;
        let offset = 0;
        if (i !== 0) {
          const aligned = alignedClipForIndex(camModalSelectedClip, camModalClipsByIndex[i] || []);
          if (!aligned) return;
          filename = aligned.filename;
          offset = aligned.offsetSec;
        }
        if (!filename) return;
        try {
          const url = `${CAMERA_BASE_URL}/recordings/file?station=${encodeURIComponent(camModalStation)}&index=${i}&filename=${encodeURIComponent(filename)}&token=${CF_ACCESS_CLIENT_SECRET}`;
          const res = await fetch(url, { headers: getCfHeaders(), credentials: 'include' });
          if (!res.ok) return;
          const blob = await res.blob();
          const objUrl = URL.createObjectURL(blob);
          createdUrls.push(objUrl);
          panes[i] = { url: objUrl, offset };
        } catch (e) {
          console.error(`Failed to load video for camera ${i}`, e);
        }
      }));

      if (cancelled) {
        createdUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } });
        return;
      }
      setCamModalVideoUrls(panes);
      setCamModalVideoLoading(false);
    }
    loadAllCamVideos();
    return () => {
      cancelled = true;
      createdUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camModalSelectedClip, camModalStation, camModalOpen, camModalCamCount]);

  function closeCamModal() {
    camModalVideoUrls.forEach(p => { if (p && p.url) { try { URL.revokeObjectURL(p.url); } catch (e) { /* noop */ } } });
    Object.values(camModalThumbs).forEach(u => { try { URL.revokeObjectURL(u); } catch (e) { /* noop */ } });
    camVideoRefs.current = [];
    setCamModalOpen(false);
    setCamModalBlock(null);
    setCamModalStation("");
    setCamModalCamCount(1);
    setCamModalClips([]);
    setCamModalClipsByIndex({});
    setCamModalSelectedClip("");
    setCamModalVideoUrls([]);
    setCamModalThumbs({});
  }

  function handleCamVideoEnded() {
    const idx = camModalClips.indexOf(camModalSelectedClip);
    if (idx >= 0 && idx < camModalClips.length - 1) {
      setCamModalSelectedClip(camModalClips[idx + 1]);
    }
  }

  // Camera 0 is the control surface; mirror its play/pause/seek to the other panes
  // so a multi-camera grid stays roughly in sync without per-frame clock tracking.
  const camOtherVideos = () => camVideoRefs.current.filter((v, i) => v && i !== 0);
  function handlePrimaryPlay() {
    camOtherVideos().forEach(v => { const p = v.play && v.play(); if (p && p.catch) p.catch(() => {}); });
  }
  function handlePrimaryPause() {
    camOtherVideos().forEach(v => { try { v.pause(); } catch (e) { /* noop */ } });
  }
  function handlePrimarySeeked() {
    const primary = camVideoRefs.current[0];
    if (!primary) return;
    camVideoRefs.current.forEach((v, i) => {
      if (!v || i === 0) return;
      const off = (camModalVideoUrls[i] && camModalVideoUrls[i].offset) || 0;
      try {
        const target = primary.currentTime + off;
        if (Math.abs(v.currentTime - target) > 0.4) v.currentTime = Math.max(0, Math.min(target, v.duration || target));
      } catch (e) { /* noop */ }
    });
  }

  const timelineSeries = [{
    name: 'Activity',
    data: timelineData.map(block => ({
      x: selectedUser ? selectedUser.name : "Activity",
      y: [
        new Date(block.start_time).getTime(),
        new Date(block.end_time).getTime()
      ],
      ...block
    }))
  }];

  const timelineOptions = {
    chart: {
      type: "rangeBar",
      toolbar: { show: true },
      events: {
        dataPointSelection: (event, chartContext, cfg) => {
          const block = timelineData[cfg.dataPointIndex];
          if (block) handleTimelineClick(block);
        }
      },
      selection: { enabled: false },
    },
    plotOptions: {
        bar: {
            horizontal: true,
            barHeight: "70%",
            distributed: true
        }
    },
    states: {
      hover: { filter: { type: 'darken', value: 0.85 } },
      active: { filter: { type: 'none' } },
    },
    colors: timelineData.map(block => workstationColors[block.workstation] || workstationColors.default),
    xaxis: {
      type: "datetime",
      labels: { datetimeUTC: false, format: 'h:mm tt' }
    },
    yaxis: {
      labels: { show: true },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    grid: {
      yaxis: { lines: { show: false } },
      xaxis: { lines: { show: true } }
    },
    tooltip: {
      custom: ({ seriesIndex, dataPointIndex, w }) => {
        const point = w.config.series[seriesIndex].data[dataPointIndex];
        const { start_time, end_time, master_job_num, job_description, product_id, part_description, workstation } = point;
        
        const startTimeFormatted = formatTimelineTooltip(start_time);
        const endTimeFormatted = formatTimelineTooltip(end_time);

        const durationMs = new Date(end_time) - new Date(start_time);
        const totalMinutes = Math.round(durationMs / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const durationStr = `${hours > 0 ? `${hours}h ` : ''}${minutes}m`;

        const baseInfo = `<div><strong>${workstation}</strong>: ${startTimeFormatted} - ${endTimeFormatted} (${durationStr})</div>`;

        if (workstation === 'Inactive') {
             return `<div class="p-2" style="font-family: sans-serif; font-size: 12px;">
                        ${baseInfo}
                     </div>`;
        }

        if (workstation === 'Laser - Fault') {
             return `<div class="p-2" style="font-family: sans-serif; font-size: 12px;">
                        ${baseInfo}
                        <div class="mt-1"><strong>Job:</strong> #${master_job_num} - ${job_description || 'N/A'}</div>
                        <div><strong>Cause:</strong> Laser fault LED detected</div>
                     </div>`;
        }

        return `<div class="p-2" style="font-family: sans-serif; font-size: 12px;">
                  ${baseInfo}
                  <div class="mt-1"><strong>Job:</strong> #${master_job_num} - ${job_description || 'N/A'}</div>
                  <div><strong>Part:</strong> ${product_id || 'N/A'} - ${part_description || 'N/A'}</div>
                </div>`;
      }
    },
    legend: { show: false }
  };


  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <div className="border-end p-3" style={{ width: "300px", flexShrink: 0, display: "flex", flexDirection: "column" }}>
        
        <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Users</h5>
                <button className="btn btn-sm btn-outline-primary" onClick={resetForm}>New</button>
            </div>
            
            {/* Search Bar */}
            <input 
                type="text" 
                className="form-control mb-2" 
                placeholder="Search users..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
            />

            {/* Inactive Toggle */}
            <div className="form-check form-switch">
                <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="showInactiveToggle"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                />
                <label className="form-check-label small text-muted" htmlFor="showInactiveToggle">
                    Show Inactive
                </label>
            </div>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && <div>Loading...</div>}
            {error && <div className="alert alert-danger">{error}</div>}
            {!loading && filteredUsers.map(u => (
            <div
                key={u.user_id}
                className={`p-2 border-bottom ${selectedUser && selectedUser.user_id === u.user_id ? "bg-primary-subtle" : "bg-white"}`}
                style={{ cursor: "pointer" }}
                onClick={() => handleSelectUser(u)}
            >
                <div className="fw-bold">{u.name}</div>
                <div style={{ fontSize: "0.9rem" }}>ID: {u.user_id} | Role: {u.role}</div>
                <div style={{ fontSize: "0.8rem" }}>{u.is_active ? "Active" : "Inactive"}</div>
                <div style={{ fontSize: "0.7rem" }}>
                Created: {u.created_at ? new Date(u.created_at).toLocaleString() : ""}
                <br />
                Updated: {u.updated_at ? new Date(u.updated_at).toLocaleString() : ""}
                </div>
                {!u.is_active && <small style={{ color: "red" }}>INACTIVE</small>}
            </div>
            ))}
            {!loading && filteredUsers.length === 0 && (
                <div className="text-muted small p-2">No users found.</div>
            )}
        </div>
      </div>

      <div className="flex-grow-1 p-4 overflow-auto">
        <h4>{selectedUser ? `Editing User: ${selectedUser.name}` : "Create New User"}</h4>
        
        <ul className="nav nav-tabs">
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
                    User Details & Reports
                </button>
            </li>
            <li className="nav-item">
                <button className={`nav-link ${activeTab === 'timesheet' ? 'active' : ''}`} onClick={() => setActiveTab('timesheet')}>
                    Labor Hours
                </button>
            </li>
        </ul>

        <div className="tab-content pt-3">
            {activeTab === 'details' && (
                <div>
                    {error && <div className="alert alert-danger">{error}</div>}

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label">Name</label>
                            <input type="text" className="form-control" value={name} onChange={e => setName(e.target.value)} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Role</label>
                            <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
                            <option value="Admin">Admin</option>
                            <option value="Operator">Operator</option>
                            <option value="Manager">Manager</option>
                            </select>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label">Hourly Wage ($/hr)</label>
                            <div className="input-group">
                                <span className="input-group-text">$</span>
                                <input 
                                    type="number" 
                                    step="0.01" 
                                    min="0"
                                    className="form-control" 
                                    value={hourlyWage} 
                                    onChange={e => setHourlyWage(e.target.value)} 
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-6">
                            <label className="form-label">{selectedUser ? "New PIN (optional)" : "PIN (4-6 digits)"}</label>
                            <input type="password" maxLength={6} className="form-control" value={pin} onChange={e => setPin(e.target.value)} placeholder="Enter 4-6 digit PIN" />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Confirm PIN</label>
                            <input type="password" maxLength={6} className="form-control" value={confirmPin} onChange={e => setConfirmPin(e.target.value)} placeholder="Re-type PIN" />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label me-2">Active?</label>
                        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
                    </div>

                    <button className="btn btn-primary me-2" onClick={handleSave} disabled={loading}>
                        {selectedUser ? "Save Changes" : "Create User"}
                    </button>
                    {selectedUser && selectedUser.is_active && (
                        <button className="btn btn-warning" onClick={() => handleMarkInactive(selectedUser)} disabled={loading}>
                            Mark Inactive
                        </button>
                    )}

                    {/* Phones that remember this Admin's unlock (30 days), with Sign out */}
                    {selectedUser && String(selectedUser.role || '').toLowerCase() === 'admin' && (
                        <RememberedPhones userId={selectedUser.user_id} userName={selectedUser.name} />
                    )}

                    {/* --- NEW: At a Glance Gauges --- */}
                    {selectedUser && <UtilizationBadges selectedUser={selectedUser} />}


                    {/* --- Bar Chart Report --- */}
                    <div className="mt-5 p-3 border rounded">
                        <h5>Task Time Report for: <span className="text-primary">{selectedUser ? selectedUser.name : 'No User Selected'}</span></h5>
                        
                        {/* --- Totals Display --- */}
                        {selectedUser && chartData.data.length > 0 && (
                            <div className="d-flex justify-content-around p-2 bg-light rounded my-3 text-center flex-wrap gap-2">
                                <div>
                                    <h6 className="mb-0 text-muted">Total Punch Time</h6>
                                    <p className="fw-bold fs-5 mb-0">{formatDecimalHoursToHHMM(reportTotals.totalPunched)}</p>
                                </div>
                                {reportTotals.totalFault > 0 && (
                                    <div>
                                        <h6 className="mb-0 text-muted">Laser Fault Time</h6>
                                        <p className="fw-bold fs-5 mb-0" style={{color: '#FF6B6B'}}>{formatDecimalHoursToHHMM(reportTotals.totalFault)}</p>
                                    </div>
                                )}
                                <div>
                                    <h6 className="mb-0 text-muted">Total Timesheet Hours</h6>
                                    <p className="fw-bold fs-5 mb-0">{formatDecimalHoursToHHMM(reportTotals.totalTimesheet)}</p>
                                </div>
                                <div>
                                    <h6 className="mb-0 text-muted">Utilization</h6>
                                    <p className="fw-bold fs-5 mb-0" style={{color: '#008FFB'}}>
                                        {reportTotals.utilization.toFixed(1)}%
                                    </p>
                                </div>
                                {reportTotals.totalFault > 0 && (
                                    <div>
                                        <h6 className="mb-0 text-muted">Adjusted Utilization</h6>
                                        <p className="fw-bold fs-5 mb-0" style={{color: '#FF6B6B'}}>
                                            {reportTotals.adjustedUtilization.toFixed(1)}%
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedUser && (
                            <>
                            <div className="row mb-3 align-items-end">
                                <div className="col-md-3">
                                <label className="form-label">Start Date</label>
                                <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                <label className="form-label">End Date</label>
                                <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                </div>
                                <div className="col-md-3">
                                <label className="form-label">Group By</label>
                                <select className="form-select" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                                    <option value="day">Day</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                </select>
                                </div>
                                <div className="col-md-3">
                                    {csvData.length > 0 && (
                                        <CSVLink
                                            data={csvData}
                                            headers={csvHeaders}
                                            filename={`${selectedUser.name}_Time_Report_${startDate}_to_${endDate}.csv`}
                                            className="btn btn-success w-100"
                                        >
                                            Export to CSV
                                        </CSVLink>
                                    )}
                                </div>
                            </div>
                            {chartLoading && <div className="text-center">Loading Chart...</div>}
                            {chartError && <div className="alert alert-warning">{chartError}</div>}
                            <div style={{ width: '100%', height: 400 }} className="mt-2">
                                <ResponsiveContainer>
                                <BarChart data={chartData.data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis yAxisId="left" label={{ value: 'Hours', angle: -90, position: 'insideLeft' }} />
                                    <YAxis yAxisId="right" orientation="right" stroke="#ff7300" label={{ value: 'Timesheet Hours', angle: 90, position: 'insideRight' }} />
                                    <YAxis 
                                        yAxisId="util" 
                                        orientation="right" 
                                        stroke="#008FFB" 
                                        unit="%"
                                        domain={[0, dataMax => Math.max(110, Math.ceil(dataMax / 10) * 10)]}
                                        label={{ value: 'Utilization', angle: -90, position: 'insideRight', offset: 45 }}
                                    />
                                    <Tooltip content={<CustomBarChartTooltip />} />
                                    <Legend />
                                    {chartData.workstations.map((ws, index) => (
                                    <Bar
                                        yAxisId="left"
                                        key={ws}
                                        dataKey={ws}
                                        stackId="a"
                                        fill={workstationBarColors[index % workstationBarColors.length]}
                                    />
                                    ))}
                                    {chartData.data.some(d => d.fault_hours > 0) && (
                                    <Bar
                                        yAxisId="left"
                                        dataKey="fault_hours"
                                        name="Laser Fault"
                                        stackId="a"
                                        fill="#FF6B6B"
                                    />
                                    )}
                                    <Line
                                        yAxisId="right"
                                        type="monotone"
                                        dataKey="kancraft_hours"
                                        name="Timesheet Hours"
                                        stroke="#ff7300"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                    <Line
                                        yAxisId="util"
                                        type="monotone"
                                        dataKey="utilization_rate"
                                        name="Utilization"
                                        stroke="#008FFB"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        activeDot={{ r: 6 }}
                                    />
                                    {chartData.data.some(d => d.fault_hours > 0) && (
                                    <Line
                                        yAxisId="util"
                                        type="monotone"
                                        dataKey="adjusted_utilization_rate"
                                        name="Adjusted Utilization"
                                        stroke="#FF6B6B"
                                        strokeWidth={2}
                                        strokeDasharray="5 5"
                                        dot={{ r: 3 }}
                                        activeDot={{ r: 5 }}
                                    />
                                    )}
                                </BarChart>
                                </ResponsiveContainer>
                            </div>
                            </>
                        )}
                    </div>

                    {/* --- Timeline Chart Report --- */}
                    <div className="mt-4 p-3 border rounded">
                        <h5>
                            Daily Activity Timeline for: <span className="text-primary">{selectedUser ? selectedUser.name : 'No User Selected'}</span>
                            {timelineData.length > 0 && (
                                <span className="ms-3 fw-normal fs-6 text-muted">
                                    (Total Active Time: {formatDecimalHoursToHHMM(totalTimelineHours)})
                                    {totalFaultHours > 0 && (
                                        <span className="ms-2" style={{color: '#FF6B6B'}}>
                                            (Fault: {formatDecimalHoursToHHMM(totalFaultHours)})
                                        </span>
                                    )}
                                </span>
                            )}
                        </h5>
                        {selectedUser && (
                            <>
                                <div className="row mb-3 align-items-end">
                                    <div className="col-md-3">
                                        <label className="form-label">Date</label>
                                        <input type="date" className="form-control" value={timelineDate} onChange={(e) => setTimelineDate(e.target.value)} />
                                    </div>
                                </div>
                                {timelineLoading && <div className="text-center">Loading Timeline...</div>}
                                {timelineError && <div className="alert alert-warning">{timelineError}</div>}
                                {!timelineLoading && !timelineError && (
                                    <>
                                        <div className="d-flex flex-wrap justify-content-center small gap-3 mb-2">
                                            {Object.entries(workstationColors).filter(([key]) => key !== 'default').map(([workstation, color]) => (
                                            <div key={workstation} className="d-flex align-items-center">
                                                <span
                                                style={{
                                                    width: "12px",
                                                    height: "12px",
                                                    backgroundColor: color,
                                                    display: "inline-block",
                                                    marginRight: "5px",
                                                    borderRadius: "3px",
                                                }}
                                                ></span>
                                                {workstation}
                                            </div>
                                            ))}
                                        </div>
                                        <div style={{ width: '100%', height: 150, cursor: 'pointer' }}>
                                            {timelineData.length > 0 ? (
                                                <ApexChart
                                                    options={timelineOptions}
                                                    series={timelineSeries}
                                                    type="rangeBar"
                                                    height={150}
                                                />
                                            ) : (
                                                <div className="d-flex align-items-center justify-content-center h-100">
                                                    <p>No activity data for the selected day.</p>
                                                </div>
                                            )}
                                        </div>
                                        {timelineData.length > 0 && (
                                            <div className="text-center mt-1">
                                                <small className="text-muted"><i className="fas fa-video me-1"></i>Click a segment to view camera footage</small>
                                            </div>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </div>
                </div>
            )}
            {activeTab === 'timesheet' && (
                <TimesheetSyncPanel />
            )}
        </div>
      </div>

      {/* ── Camera Playback Modal ── */}
      {camModalOpen && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={(e) => { if (e.target === e.currentTarget) closeCamModal(); }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={{ backgroundColor: '#1a1a1a', border: '1px solid #444' }}>
              <div className="modal-header py-2" style={{ borderBottom: '1px solid #444' }}>
                <h6 className="modal-title text-white m-0">
                  <i className="fas fa-video me-2 text-danger"></i>
                  {camModalBlock && (
                    <>
                      {camModalBlock.workstation === 'Inactive' ? (
                        <>Inactive — Last Station: <span className="text-info">{camModalStation || 'Unknown'}</span></>
                      ) : (
                        <span className="text-info">{camModalBlock.workstation}</span>
                      )}
                      <span className="text-muted ms-3" style={{ fontSize: '0.85rem' }}>
                        {formatTimelineTooltip(camModalBlock.start_time)} – {formatTimelineTooltip(camModalBlock.end_time)}
                      </span>
                      {camModalBlock.master_job_num && camModalBlock.workstation !== 'Inactive' && (
                        <span className="text-muted ms-3" style={{ fontSize: '0.85rem' }}>
                          Job #{camModalBlock.master_job_num}
                          {camModalBlock.product_id && <> — {camModalBlock.product_id}</>}
                        </span>
                      )}
                    </>
                  )}
                </h6>
                <button type="button" className="btn-close btn-close-white" onClick={closeCamModal}></button>
              </div>
              <div className="modal-body p-0" style={{ backgroundColor: '#000' }}>
                {!camModalStation ? (
                  <div className="text-center text-muted p-5">
                    <i className="fas fa-video-slash mb-3" style={{ fontSize: 32 }}></i>
                    <div>No camera available for this station</div>
                  </div>
                ) : camModalClipsLoading ? (
                  <div className="text-center text-muted p-5">
                    <div className="spinner-border spinner-border-sm text-light me-2" role="status"></div>
                    Loading camera clips...
                  </div>
                ) : camModalClips.length === 0 ? (
                  <div className="text-center text-muted p-5">
                    <i className="fas fa-film mb-3" style={{ fontSize: 32 }}></i>
                    <div>No recordings found for this time period</div>
                  </div>
                ) : (
                  <>
                    <div
                      style={camModalCamCount > 1
                        ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 4, padding: 4, minHeight: 300 }
                        : { minHeight: 300 }}
                    >
                      {Array.from({ length: Math.max(1, camModalCamCount) }).map((_, i) => {
                        const pane = camModalVideoUrls[i] || { url: '', offset: 0 };
                        const url = pane.url;
                        const paneMaxH = camModalCamCount > 1 ? '40vh' : '60vh';
                        const paneMinH = camModalCamCount > 1 ? 200 : 300;
                        return (
                          <div
                            key={i}
                            style={{
                              position: 'relative',
                              backgroundColor: '#000',
                              minHeight: paneMinH,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {camModalVideoLoading && !url ? (
                              <div className="text-center text-muted p-3">
                                <div className="spinner-border spinner-border-sm text-light" role="status"></div>
                              </div>
                            ) : url ? (
                              <video
                                ref={(el) => { camVideoRefs.current[i] = el; }}
                                key={`${i}-${url}`}
                                src={url}
                                controls
                                autoPlay
                                muted={i !== 0}
                                playsInline
                                onLoadedMetadata={(e) => {
                                  if (pane.offset > 0) {
                                    try {
                                      const dur = e.currentTarget.duration || pane.offset;
                                      e.currentTarget.currentTime = Math.max(0, Math.min(pane.offset, dur));
                                    } catch (err) { /* noop */ }
                                  }
                                }}
                                onEnded={i === 0 ? handleCamVideoEnded : undefined}
                                onPlay={i === 0 ? handlePrimaryPlay : undefined}
                                onPause={i === 0 ? handlePrimaryPause : undefined}
                                onSeeked={i === 0 ? handlePrimarySeeked : undefined}
                                style={{ width: '100%', maxHeight: paneMaxH, backgroundColor: '#000' }}
                              />
                            ) : (
                              <div className="text-center text-muted p-3" style={{ fontSize: '0.78rem' }}>
                                <i className="fas fa-video-slash mb-1" style={{ fontSize: 18 }}></i>
                                <div>No footage<br />for this angle</div>
                              </div>
                            )}
                            {camModalCamCount > 1 && (
                              <div style={{
                                position: 'absolute', top: 4, left: 4,
                                backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff',
                                fontSize: '0.7rem', padding: '1px 6px', borderRadius: 3,
                                pointerEvents: 'none',
                              }}>
                                {camModalStation} {i + 1}{i === 0 ? ' · main' : ''}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ borderTop: '1px solid #333', padding: '8px 12px', backgroundColor: '#111', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                      <div className="d-flex gap-2">
                        {camModalClips.map((clip) => {
                          const isActive = camModalSelectedClip === clip;
                          return (
                            <div
                              key={clip}
                              onClick={() => setCamModalSelectedClip(clip)}
                              style={{
                                cursor: 'pointer',
                                flex: '0 0 auto',
                                textAlign: 'center',
                                borderRadius: 4,
                                border: isActive ? '2px solid #dc3545' : '2px solid transparent',
                                padding: 2,
                              }}
                            >
                              <div style={{
                                width: 120, height: 68,
                                backgroundColor: '#222',
                                borderRadius: 3,
                                overflow: 'hidden',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                              }}>
                                {camModalThumbs[clip] ? (
                                  <img src={camModalThumbs[clip]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <i className="fas fa-image text-secondary" style={{ fontSize: 16 }}></i>
                                )}
                                {isActive && (
                                  <div style={{
                                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    backgroundColor: 'rgba(0,0,0,0.4)',
                                  }}>
                                    <i className="fas fa-play text-white" style={{ fontSize: 16 }}></i>
                                  </div>
                                )}
                              </div>
                              <div className={`mt-1 ${isActive ? 'text-white' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                                {formatClipTime(clip)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer py-2 justify-content-between" style={{ borderTop: '1px solid #444', backgroundColor: '#1a1a1a' }}>
                <small className="text-muted">
                  {camModalClips.length > 0 && (
                    <>{camModalClips.indexOf(camModalSelectedClip) + 1} of {camModalClips.length} clips</>
                  )}
                </small>
                <div className="d-flex gap-2">
                  {camModalClips.length > 1 && (
                    <>
                      <button
                        className="btn btn-sm btn-outline-light"
                        disabled={camModalClips.indexOf(camModalSelectedClip) <= 0}
                        onClick={() => {
                          const idx = camModalClips.indexOf(camModalSelectedClip);
                          if (idx > 0) setCamModalSelectedClip(camModalClips[idx - 1]);
                        }}
                      >
                        <i className="fas fa-chevron-left me-1"></i>Prev
                      </button>
                      <button
                        className="btn btn-sm btn-outline-light"
                        disabled={camModalClips.indexOf(camModalSelectedClip) >= camModalClips.length - 1}
                        onClick={() => {
                          const idx = camModalClips.indexOf(camModalSelectedClip);
                          if (idx < camModalClips.length - 1) setCamModalSelectedClip(camModalClips[idx + 1]);
                        }}
                      >
                        Next<i className="fas fa-chevron-right ms-1"></i>
                      </button>
                    </>
                  )}
                  <button className="btn btn-sm btn-secondary" onClick={closeCamModal}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}